import React, { useEffect, useState, useMemo, useCallback, memo } from 'react'
import { isMobile } from 'react-device-detect'
import TagManager from 'react-gtm-module'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { styled } from 'styled-components'
import { LikeProductItem, ProductSearchItem, LikeProductStickerInfo } from '../../@types/product'
import { DataLayerArgs } from '../../@types/dataLayerObject'
import { sendSetAppsflyerEvent } from '../../js/postMessage'
import { formatNumberWithTruncation, onErrorImg, plusComma, setPrevList, setPrevPage } from '../../js/script'
import LikeBtn from '../btn/LikeBtn'
import { useAppContext } from '../provider/AppProvider'
import { useCommonContext } from '../provider/CommonProvider'
import { IconStarOn } from '../svg/IconSvg'
import {
  BtnCart,
  DiscountedPrice,
  ProductBrandTitile,
  ProductItemWrap,
  ProductSalePrice,
  ProductTitle,
} from './ProductItem'
import { MultiLevelCategory } from '@shopby/shop-sdk'

const imgURL = '/images'

type PropsType = {
  item: LikeProductItem
  setIsModal: React.Dispatch<React.SetStateAction<boolean>>
  setSelectProductNo: React.Dispatch<React.SetStateAction<string>>
  page?: number
  listItem?: ProductSearchItem[]
  idx: number
  totalCount: number
  promotion_id?: number
  promotion_name?: string
  categoryData?: any
  categoryDepth1?: MultiLevelCategory
  categoryDepth2?: MultiLevelCategory
  categoryDepth3?: MultiLevelCategory
  depthLabel?: string | undefined
  defaultKeyword?: string
  setCartIdx: React.Dispatch<React.SetStateAction<number>>
}

const ProductListItem = ({
  item,
  setIsModal,
  setSelectProductNo,
  idx,
  listItem,
  page,
  totalCount,
  promotion_id,
  promotion_name,
  categoryData,
  categoryDepth1,
  categoryDepth2,
  categoryDepth3,
  depthLabel,
  defaultKeyword,
  setCartIdx,
}: PropsType) => {
  const [text, setText] = useState<LikeProductStickerInfo[]>([])
  const [image, setImage] = useState<LikeProductStickerInfo[]>([]) // 아이콘이 상단이미지로 쓰는 경우
  const [disCountAcc, setDisCountAcc] = useState(0)
  const { isAppView } = useAppContext()
  const { hasAccessToken } = useCommonContext()
  const navigate = useNavigate()
  const location = useLocation()

  const getMaxBenefitPrice = (item: any, disCountAcc: number) => {
    return item?.salePrice -
      item?.immediateDiscountAmt -
      item?.couponDiscountAmt -
      item?.accumulationInfo?.amount -
      (item?.accumulationUseInfo?.usable ? disCountAcc : 0) <
      0
      ? 0
      : item?.salePrice -
          item?.immediateDiscountAmt -
          item?.couponDiscountAmt -
          item?.accumulationInfo?.amount -
          (item?.accumulationUseInfo?.usable ? disCountAcc : 0)
  }

  const showMaxBenefit = useMemo(() => {
    if (!item.enableCoupons && !item?.accumulationUseInfo?.usable) return false
    const maxBenefitPrice = getMaxBenefitPrice(item, disCountAcc)
    return maxBenefitPrice !== item.salePrice - item.immediateDiscountAmt
  }, [item, disCountAcc])

  const linkClickHandler = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      const eventType = location.pathname.includes('exhibition')
        ? 'select_promotion'
        : location.pathname.includes('searchResult')
        ? 'select_search_item'
        : 'select_item'
      const gtmClickItem = getGtmClickItem(eventType, {
        search_term: defaultKeyword,
        search_results_count: totalCount,
        click_index: idx,
      })
      gtmEventHandler(eventType, gtmClickItem)
    },
    [location.pathname, defaultKeyword, totalCount, idx]
  )

  const gtmEventHandler = useCallback(
    (eventType: string, gtmClickItem: DataLayerArgs) => {
      const gtmItems = [...(gtmClickItem?.dataLayer?.ecommerce?.items || [])]
      // FIXME: 다르게 동작한다면 위 코드 삭제 후 아래코드 적용해주세요.
      // const gtmItems: any = [];
      // if (gtmClickItem?.dataLayer?.ecommerce?.items) {
      //   gtmItems.push(...gtmClickItem.dataLayer.ecommerce.items);
      // }
      if (isAppView) {
        sendSetAppsflyerEvent({
          eventName: eventType,
          eventType: gtmClickItem?.dataLayer?.ecommerce,
        })
      } else {
        TagManager.dataLayer(gtmClickItem)
      }

      navigate(`/product/${String(item?.productNo)}`, {
        state: { items: gtmItems },
      })

      setPrevList(listItem)
      setPrevPage(page)
    },
    [isAppView, item?.productNo, listItem, page]
  )

  const getGtmClickItem = useCallback(
    (event: string, additionalData: any = {}) => ({
      dataLayer: {
        event,
        ecommerce: {
          item_list_count: totalCount,
          items: [
            {
              item_id: String(item?.productNo),
              item_name: item?.productName,
              affiliation: '어바웃피싱',
              item_list_price: item?.salePrice,
              item_selling_price: item?.salePrice - item?.immediateDiscountAmt,
              price: item?.salePrice - item?.immediateDiscountAmt,
              item_total_discount: item?.immediateDiscountAmt,
              discount: 0,
              index: idx,
              item_brand: item?.brandName,
              item_category: !categoryData?.depth1Label ? '전체' : categoryData?.depth1Label,
              item_category2: categoryData?.depth2Label,
              item_category3: categoryData?.depth3Label,
              promotion_id: String(promotion_id),
              promotion_name: promotion_name,
              ...additionalData,
            },
          ],
        },
      },
    }),
    [totalCount, item, categoryData, promotion_id, promotion_name, idx]
  )

  useEffect(() => {
    if (!item) return
    const count = formatNumberWithTruncation((item.salePrice - item.immediateDiscountAmt) * 0.1)
    setDisCountAcc(Number(count.replaceAll(',', '')))

    const newImages = item.stickerInfos?.filter((element: any) => element.type === 'IMAGE') || []
    const newTexts = item.stickerInfos?.filter((element: any) => element.type !== 'IMAGE') || []

    setImage(newImages) // 한 번에 상태 업데이트
    setText(newTexts)
  }, [item])

  const renderBtnCart = useCallback(
    (isSoldOut: boolean, item: any, idx: number) => {
      const handleClick = () => {
        setIsModal(true)
        setSelectProductNo(String(item.productNo))
        if (!isSoldOut) {
          setCartIdx(idx)
        }
      }

      return (
        <BtnCart className='btn_cart' onClick={handleClick}>
          {isSoldOut ? (
            !isMobile && '재입고 알림'
          ) : (
            <>
              <img src={`${imgURL}/icon_cart.png`} alt='' /> {!isMobile && '장바구니'}
            </>
          )}
        </BtnCart>
      )
    },
    [setIsModal, setSelectProductNo, setCartIdx]
  )

  return (
    <div className='product_item_wrap list'>
      <div className='product_image_area'>
        <Link to={`/product/${item.productNo}`} onClick={linkClickHandler}>
          {item.isSoldOut && (
            <div className='soldout'>
              <img src={`${imgURL}/img_soldout.png`} alt='' />
            </div>
          )}
          <div className='dim'></div>
          <img src={item.listImageUrls[0]} alt='' onError={onErrorImg} />
        </Link>
        {image.map((element: any, index: number) => {
          return (
            <div key={`${element.label}-${index}`} className='item_flag'>
              <img src={element.label} alt='' />
            </div>
          )
        })}
      </div>

      <div className='list_content_wrap'>
        <Link to={`/product/${item.productNo}`} onClick={linkClickHandler} className='product_item'>
          {item.brandName && <div className='product_brand_title'>{item.brandName}</div>}
          <div className='product_title'>{item.productName}</div>
          <div className='product_sale_price'>{plusComma(item.salePrice)}원</div>
          <div className='discounted_price'>
            <p className='percent'>{Math.floor((item.immediateDiscountAmt / item.salePrice) * 100)}%</p>
            <p>{item.salePrice ? plusComma(item.salePrice - item.immediateDiscountAmt) : 0}원</p>
          </div>
          {showMaxBenefit && (
            <div className='max_benefit'>
              <p className='badge'>회원혜택가</p>
              <p>{plusComma(getMaxBenefitPrice(item, disCountAcc))}원</p>
            </div>
          )}
          {hasAccessToken && item?.accumulationInfo?.amount > 0 && (
            <div className='save_mileage'>
              <p>
                {`페이백 적립금 ${item?.accumulationInfo.rewardRateOfProduct}% `}
                {plusComma(item?.accumulationInfo?.amount)}
                <img src={`${imgURL}/icon_point.png`} alt='' />
              </p>
            </div>
          )}
          <div className='star_rating'>
            <IconStarOn width={isMobile ? 14 : 16} height={isMobile ? 14 : 16} />
            <span className='review_rating'>{item?.reviewRating}</span>
            <span className='review_num'>({item?.totalReviewCount})</span>
            <div className='npay'>
              <img src={`${imgURL}/icon_npay.png`} alt='Naver Pay' />
            </div>
          </div>
          {text && (
            <div className='item_txt_flag_detail'>
              {text.map((el: any, index: number) => (
                <span className='delivery' key={index}>
                  {el.label}
                </span>
              ))}
            </div>
          )}
        </Link>
      </div>
      <div className='button_area'>
        {renderBtnCart(item?.isSoldOut, item, idx)}
        <LikeBtn
          item={item}
          text={'찜하기'}
          categoryDepth1={categoryDepth1}
          categoryDepth2={categoryDepth2}
          categoryDepth3={categoryDepth3}
          depthLabel={depthLabel}
          idx={idx}
        />
      </div>
    </div>
  )
}

export default memo(ProductListItem)

export const ProductListItemWrap = styled(ProductItemWrap)`
  display: flex;
  flex-direction: row;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--colGray3);
  &:last-child {
    border-bottom: none;
    margin-bottom: 48px;
  }
  @media screen and (min-width: 768px) {
    gap: 24px;
  }
`

export const ProductListImageArea = styled.div`
  width: 100px;
  min-width: 100px;
  height: 100px;
  min-height: 100px;
  @media screen and (min-width: 768px) {
    width: 200px;
    height: 200px;
    min-width: 200px;
    min-height: 200px;
  }
`

export const BtnListCart = styled(BtnCart)`
  width: 32px;
  height: 32px;
  margin-top: 8px;
  @media screen and (min-width: 768px) {
    width: 105px;
    height: 32px;
  }
`

export const ListContentWrap = styled.div`
  flex: 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-left: 3px;
  min-width: 166.5px;
`

// 브랜드 제목
export const ProductListBrandTitile = styled(ProductBrandTitile)`
  margin-bottom: 2px;
  @media screen and (min-width: 768px) {
    font-size: 14px;
    font-weight: 600;
    line-height: 16px;
    color: var(--colBlack);
    margin-bottom: 4px;
  }
`
// 상품 제목
export const ProductListTitile = styled(ProductTitle)`
  margin-bottom: 4px;
  font-size: 14px;
  @media screen and (min-width: 768px) {
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;
    color: var(--colBlack);
    margin-bottom: 8px;
  }
`
// 상품 원래 가격
export const ProductSaleListPrice = styled(ProductSalePrice)`
  margin-bottom: 2px;
  @media screen and (min-width: 768px) {
    font-size: 14px;
    font-weight: 500;
    line-height: 16px;
    color: var(--colGray6);
  }
`
// 할인된 금액
export const DiscountedListPrice = styled(DiscountedPrice)`
  margin-bottom: 4px;
  @media screen and (min-width: 768px) {
    & p {
      font-size: 18px;
      font-weight: 600;
      line-height: 22px;
      color: var(--colBlack);
    }
    & p.percent {
      color: var(--colPointOrange);
    }
  }
`
